// Tenant brand tokens.
//
// Branding reaches the interface as two CSS custom properties and nothing
// else. That is what keeps it bounded: only the surfaces that explicitly
// read `var(--regure-brand-*)` can change, and that list is short and
// deliberate —
//
//   • the active navigation item (indicator bar, label, icon)
//   • the primary action button
//
// Everything else — layout, typography, the Regure mark's fallback, status
// colours, charts, the whole shell — stays Regure's. A tenant cannot
// restyle the application, only mark it as theirs.
//
// The contrast guard is duplicated from the server on purpose. The API
// already refuses an unreadable colour, but a value could predate that
// validation or arrive from a seed script, and the cost of being wrong is
// an unreadable interface. Anything that fails re-check falls back to the
// Regure palette rather than being applied.

export const REGURE_PRIMARY = '#800020';
export const REGURE_ACCENT  = '#722f37';

const VAR_PRIMARY = '--regure-brand-primary';
const VAR_ACCENT  = '--regure-brand-accent';

const HEX = /^#[0-9a-fA-F]{6}$/;
const MIN_CONTRAST = 4.5;

function luminance(hex) {
  const c = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255)
    .map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

/** Contrast against white — these colours carry white text and sit on white. */
export function contrastWithWhite(hex) {
  return 1.05 / (luminance(hex) + 0.05);
}

/** A colour is usable only if it is well-formed AND readable. */
export function usableBrandColor(raw) {
  const v = String(raw || '').trim().toLowerCase();
  if (!HEX.test(v)) return null;
  if (contrastWithWhite(v) < MIN_CONTRAST) return null;
  return v;
}

/**
 * Push the tenant's colours onto the document, or restore Regure's.
 *
 * Called on login and after a Settings save, alongside the vocabulary
 * update, so a save repaints without a reload.
 */
export function applyBrandTokens(branding) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const primary = usableBrandColor(branding?.primaryColor) || REGURE_PRIMARY;
  // The accent only ever appears next to the primary (second gradient stop,
  // active indicator), so an unset accent follows the primary rather than
  // reverting to Regure's — otherwise a branded button would end in
  // Regure burgundy.
  const accent = usableBrandColor(branding?.accentColor)
    || (primary === REGURE_PRIMARY ? REGURE_ACCENT : primary);

  root.style.setProperty(VAR_PRIMARY, primary);
  root.style.setProperty(VAR_ACCENT, accent);
}

/** Restore Regure's palette — on logout, so the next user never inherits it. */
export function resetBrandTokens() {
  applyBrandTokens(null);
}

export default { applyBrandTokens, resetBrandTokens, usableBrandColor };
